import { RootState } from "src/store";
import { connect } from "react-redux";
import { Autocomplete, TextField } from "@mui/material";
import { useMemo } from "react";
import { NO_LABEL_FILTER_ID, TCustomFilter } from "../../filter-types";
import { useConfiguratorPageT } from "src/translation-hooks/configurator-page";
import { filterActions } from "../../filter-store";

const mapStateToProps = (state: RootState) => ({
  labels: state.pageReducer.combinedConfiguratorReducer.configuratorReducer.labels,
  formData: state.pageReducer.combinedConfiguratorReducer.filterReducer.formData
});

const ownActions = {
  setFormDataCustomFilters: filterActions.setFormDataCustomFilters
};

type Props = typeof ownActions & ReturnType<typeof mapStateToProps>;

const LabelsFilterBase = (props: Props) => {
  const { labels, setFormDataCustomFilters, formData } = props;

  const configuratorPageT = useConfiguratorPageT();

  const selectedLabelFilters = (formData.customFilters || []).filter(x => x.filterType === "label");

  // derive label options from already-loaded labels state (no extra API call needed)
  const labelOptions = useMemo<TCustomFilter[]>(() => {
    const noLabelOption: TCustomFilter = { id: NO_LABEL_FILTER_ID, description: configuratorPageT.groupByUnlabelled, filterType: "label" as const };
    const realLabelOptions = (labels ?? [])
      .slice()
      .sort((a, b) => a.shortname.localeCompare(b.shortname))
      .map(l => ({ id: l.labelId, description: l.shortname, filterType: "label" as const }));
    return [noLabelOption, ...realLabelOptions];
  }, [labels, configuratorPageT.groupByUnlabelled]);

  return (
    <Autocomplete<TCustomFilter, true>
      value={selectedLabelFilters}
      onChange={(_, values) => {
        // preserve all other filter types when label selection changes
        const otherItems = (formData.customFilters || []).filter(x => x.filterType !== "label");
        setFormDataCustomFilters([...otherItems, ...values]);
      }}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      getOptionDisabled={option => selectedLabelFilters.some(selected => selected.id === option.id)}
      size="small"
      multiple
      options={labelOptions}
      getOptionLabel={option => option.description || ""}
      filterSelectedOptions
      renderInput={params => <TextField {...params} label={configuratorPageT.label} />}
    />
  );
};

export const LabelsFilter = connect(mapStateToProps, ownActions)(LabelsFilterBase);
